import ReactNativeBiometrics from 'react-native-biometrics';
import { ACCESS_CONTROL, getGenericPassword, resetGenericPassword, setGenericPassword } from 'react-native-keychain';

import i18n from '@webtrader/core/i18n';
import storage, { EStorageKey } from '@webtrader/core/storage';

import { checkIsIos } from './device';

export enum EBiometricsType {
  BIOMETRICS = 'BIOMETRICS',
  FACE_ID = 'FACE_ID',
  TOUCH_ID = 'TOUCH_ID',
  NOT_SUPPORT = 'NOT_SUPPORT'
}

export const BIOMETRICS_SETTINGS_LABELS = {
  BIOMETRICS: {
    title: () => i18n.t('Biometrics'),
    description: () => i18n.t('Enable Biometrics to login')
  },
  FACE_ID: {
    title: () => i18n.t('Face ID'),
    description: () => i18n.t('Enable Face ID to login')
  },
  TOUCH_ID: {
    title: () => i18n.t('Touch ID'),
    description: () => i18n.t('Enable Touch ID to login')
  },
  NOT_SUPPORT: {
    title: () => i18n.t('Biometrics'),
    description: () => i18n.t('Biometrics not supported on this device')
  }
};

export const mapBiometricsSettingsToDisplay = (biometryType?: string) => {
  switch (biometryType) {
    case EBiometricsType.BIOMETRICS:
      return BIOMETRICS_SETTINGS_LABELS.BIOMETRICS;
    case EBiometricsType.FACE_ID:
      return BIOMETRICS_SETTINGS_LABELS.FACE_ID;
    case EBiometricsType.TOUCH_ID:
      return BIOMETRICS_SETTINGS_LABELS.TOUCH_ID;
    default:
      return BIOMETRICS_SETTINGS_LABELS.NOT_SUPPORT;
  }
};

export const getBiometricEnable = () => {
  const isEnable = storage.getItem(EStorageKey.BIOMETRICS_ENABLE) as string;

  return isEnable === 'true';
};

export const storeBiometricEnable = (enable: boolean) => {
  storage.setItem(EStorageKey.BIOMETRICS_ENABLE, JSON.stringify(enable));
};

export const syncBiometricCredentials = async (newUserId: string, newPassword: string) => {
  const biometricsEnabled = getBiometricEnable();

  if (!biometricsEnabled) {
    return;
  }

  const userId = storage.getItem(EStorageKey.USER_ID) as string;

  if (userId) {
    if (userId !== newUserId) {
      if (checkIsIos()) {
        await iosBiometricsHandler(newUserId, newPassword, () => {});
      } else {
        await androidBiometricsHandler(newUserId, newPassword, () => {});
      }
    } else {
      storage.setItem(EStorageKey.PASSWORD, newPassword);
      forceSetBiometricPassword(() => {});
    }
  }
};

export const forceSetBiometricPassword = async (setEnable: (value: boolean) => void) => {
  const userId = storage.getItem(EStorageKey.USER_ID) as string;
  const password = storage.getItem(EStorageKey.PASSWORD) as string;

  if (userId && password) {
    if (checkIsIos()) {
      iosBiometricsHandler(userId, password, setEnable);
    } else {
      androidBiometricsHandler(userId, password, setEnable);
    }
  }
};

export const setBiometricGeneric = async (enable: boolean, setEnable: (value: boolean) => void) => {
  const userId = storage.getItem(EStorageKey.USER_ID) as string;
  const password = storage.getItem(EStorageKey.PASSWORD) as string;

  if (userId && password && enable) {
    if (checkIsIos()) {
      iosBiometricsHandler(userId, password, setEnable);
    } else {
      androidBiometricsHandler(userId, password, setEnable);
    }
  } else {
    storeBiometricEnable(false);
    setEnable(false);
  }
};

const iosBiometricsHandler = async (userId: string, password: string, setEnable: (value: boolean) => void) => {
  // Handling for ios is different with android
  // 1. Reset the credential from Keychain
  await resetGenericPassword();

  // 2. Set the new credential to Keychain
  await setGenericPassword(userId, password, {
    accessControl: ACCESS_CONTROL.BIOMETRY_ANY_OR_DEVICE_PASSCODE
  });

  // 3. Retrieve the credential from Keychain, and set enable if verified
  try {
    const credentials = await getGenericPassword(biometricsPasscodeOptions);

    if (credentials) {
      storeBiometricEnable(true);
      setEnable(true);
    } else {
      storeBiometricEnable(false);
      setEnable(false);
    }
  } catch (error) {
    storeBiometricEnable(false);
    setEnable(false);
  }
};

const androidBiometricsHandler = async (userId: string, password: string, setEnable: (value: boolean) => void) => {
  if (__WEB__) {
    return;
  }
  // Handling for android
  // 1. Trigger biometrics
  const resultObject = await ReactNativeBiometrics.simplePrompt({
    promptMessage: i18n.t('Authentication needed')
  });

  const { success } = resultObject;

  // 2. Set enable if verified
  if (success) {
    await setGenericPassword(userId, password);

    storeBiometricEnable(true);
    setEnable(true);
  } else {
    storeBiometricEnable(false);
    setEnable(false);
  }
};

export const biometricsPasscodeOptions = {
  authenticationPrompt: {
    title: i18n.t('Authentication needed'),
    cancel: i18n.t('Cancel')
  }
};
